import Big from 'big.js';
import { dropWhile, takeWhile } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useConstant } from '../../../hooks/useConstant';
import { useDynamicCallback } from '../../../hooks/useDynamicCallback';
import { Button, IconButton } from '../../Button';
import { Flex } from '../../Core';
import { IconName } from '../../Icons';
import { Text } from '../../Text';
import {
  AutoCompleteDropdownGroupHeader,
  type AutocompleteGroup,
  type AutocompleteSearchResult,
} from '../Autocomplete';
import { SearchSelect } from '../SearchSelect';
import type { SearchSelectRef } from '../SearchSelect/types';
import { FormControlSizes } from '../types';

export type RangeSelectObject = {
  from?: string | null;
  to?: string | null;
};

export function RangeSelect({
  title,
  range,
  getLabel,
  options,
  onChange,
  // grabs data-testid and propagates it down.
  ...props
}: {
  title: string;
  range: RangeSelectObject | undefined;
  getLabel: (v: string) => string;
  options: string[];
  onChange: (range: RangeSelectObject | undefined) => void;
}) {
  // For ease of use, the ´to´ dropdown should open after 'from' has been set
  // IFF it has not been manually set by the user at some point.
  const [shouldOpenToAfterFrom, setShouldOpenToAfterFrom] = useState(false);

  const fromOptions = useMemo(() => {
    const to = range?.to;
    if (to) {
      return takeWhile(options, s => Big(s).lte(to));
    }
    return options;
  }, [range?.to, options]);

  const toOptions = useMemo(() => {
    const from = range?.from;
    if (from) {
      return dropWhile(options, s => Big(s).lt(from));
    }
    return options;
  }, [range?.from, options]);

  const handleOnFromChange = useDynamicCallback((newFrom: string | undefined) => {
    onChange({ from: newFrom ?? null });
    if (shouldOpenToAfterFrom) {
      toSelectRef.current?.openMenu();
    }
  });

  const handleOnToChange = useDynamicCallback((newTo: string | undefined) => {
    onChange({ to: newTo ?? null });
    setShouldOpenToAfterFrom(false);
  });
  const handleClear = useDynamicCallback(() => {
    onChange(undefined);
  });

  const fromSelectRef = useRef<SearchSelectRef>(null);

  const onRangeSelect = useDynamicCallback(() => {
    onChange({
      from: fromOptions.at(0),
      to: toOptions.at(-1),
    });
    setShouldOpenToAfterFrom(true);
    setTimeout(() => {
      fromSelectRef.current?.openMenu();
    }, 0);
  });

  const toSelectRef = useRef<SearchSelectRef>(null);

  const fromSelectStyle = useConstant({ borderEndEndRadius: 0, borderStartEndRadius: 0 });
  const toSelectStyle = useConstant({ borderRadius: 0 });
  const iconStyle = useConstant({ borderEndStartRadius: 0, borderStartStartRadius: 0 });

  const getFromGroup = useCallback(() => 'From', []);
  const getToGroup = useCallback(() => 'To', []);
  const GroupRenderer = useCallback(({ group }: { group: AutocompleteGroup<AutocompleteSearchResult<string>> }) => {
    return <AutoCompleteDropdownGroupHeader group={group} showCount={false} />;
  }, []);

  const prefix = useMemo(
    () => (
      <Text key={title} mr="spacing4" color="colors.gray.090" fontWeight={500} whiteSpace="nowrap">
        {title}
      </Text>
    ),
    [title]
  );

  return (
    <span {...props}>
      {range === undefined ? (
        <Button data-testid="range-button" dim size={FormControlSizes.Small} onClick={onRangeSelect}>
          {title}
        </Button>
      ) : (
        <Flex gap="1px">
          <SearchSelect
            portalize
            getGroup={getFromGroup}
            RenderGroupHeader={GroupRenderer}
            selection={range?.from || undefined}
            getLabel={getLabel}
            initialSortByLabel={false}
            onChange={handleOnFromChange}
            options={fromOptions}
            prefix={prefix}
            size={FormControlSizes.Small}
            selectorStyle={fromSelectStyle}
            ref={fromSelectRef}
            gridTemplateColumns="repeat(3, min-content)"
            maxHeight={200}
            groupMaxHeight={180}
            data-testid="range-select-from"
          />
          <SearchSelect
            portalize
            getGroup={getToGroup}
            RenderGroupHeader={GroupRenderer}
            selection={range?.to || undefined}
            getLabel={getLabel}
            initialSortByLabel={false}
            onChange={handleOnToChange}
            options={toOptions}
            size={FormControlSizes.Small}
            selectorStyle={toSelectStyle}
            ref={toSelectRef}
            gridTemplateColumns="repeat(3, min-content)"
            maxHeight={200}
            groupMaxHeight={180}
            data-testid="range-select-to"
          />

          <IconButton size={FormControlSizes.Small} icon={IconName.Close} onClick={handleClear} style={iconStyle} />
        </Flex>
      )}
    </span>
  );
}
